import clsx from 'clsx'
import React from 'react'

import Button from 'lib/components/Button/Button'
import Icon from 'lib/components/Icon/Icon'
import { IconNames } from 'lib/settings/icons'
import { clickActionButton } from 'lib/utils/orderUtils'
import { OrderTrackingButtonItem } from './orderTrackingButtons'

// Match the structure from orderTrackingButtons
interface OrderActionButtonsProps {
  buttons: OrderTrackingButtonItem[]
  orderId: string
  isLoggedIn?: boolean
  isGuestUser?: boolean
  className?: string
  showButtons?: boolean
  hasGuestIdParam?: boolean
  /**
   * Modal opener function (for buttons with modal: true)
   */
  openModal?: (modalId: string) => void
}

const OrderActionButtons: React.FC<OrderActionButtonsProps> = ({
  buttons,
  orderId,
  isLoggedIn = false,
  isGuestUser = false,
  className,
  showButtons = true,
  hasGuestIdParam = false,
  openModal,
}) => {
  // Filter buttons based on login status (matches parent component logic)
  const filteredButtons = React.useMemo(() => {
    return buttons.filter((button) => {
      if (button.hideForGuest && isGuestUser) {
        return false
      }
      if (button.loggedInOnly) {
        if (
          hasGuestIdParam &&
          (button.id === 'print-receipt' || button.id === 'share-itinerary')
        ) {
          return true
        }
        return isLoggedIn
      }
      return true
    })
  }, [buttons, hasGuestIdParam, isLoggedIn, isGuestUser])

  const handleButtonClick = React.useCallback(
    (button: OrderTrackingButtonItem, orderIdParam: string) => {
      clickActionButton(button, orderIdParam, openModal)
    },
    [openModal],
  )

  // Don't render if showButtons is false or no buttons to show
  if (!showButtons || filteredButtons.length === 0) {
    return null
  }

  return (
    <div
      className={clsx(
        'md-max:mt-4 flex flex-wrap gap-[16px] md-max:whitespace-nowrap',
        className,
      )}
    >
      {filteredButtons.map((button) => (
        <Button
          icon={button.icon as IconNames}
          key={button.id}
          type="button"
          variant="tertiary"
          className={clsx(
            '[&]:p-2 [&]:lg:p-4 [&]:md-max:w-[138px]',
            button.className,
          )}
          onClick={() => handleButtonClick(button, orderId)}
        >
          {button.icon && <Icon name={button.icon} />}
          {button.text}
        </Button>
      ))}
    </div>
  )
}

export default OrderActionButtons
